import { createSelector } from 'reselect';

import {
  DataKiGraph,
  DataKiGraphCollection,
  DataKiGraphFieldItem,
  DataKiGraphParams,
  DataKiGraphSlotConfig,
  GRAPH_DATE_TYPE,
  GRAPH_FIELD_TYPE,
  GraphDateType,
  GraphFieldType,
  filterGraphDataBySelectHalls,
} from '../../domain/dataKiGraph';
import { buildGraphListForNormalizedSlots } from '../../domain/dataKiGraphSlots';
import { DkSisSearchParams } from '../../domain/dkSis';
import { LoadingState, QueryParameter } from '../../domain/schemas';

import { RootState } from '../../store';
import { ModelReportsSettingState } from '../ui/modelReportsSetting';

// Action Types

const SHOW_DATA_KI_GRAPH_LOADING = 'SHOW_DATA_KI_GRAPH_LOADING' as const;
const HIDE_DATA_KI_GRAPH_LOADING = 'HIDE_DATA_KI_GRAPH_LOADING' as const;
const CHANGE_DATA_KI_GRAPH_FIELD = 'CHANGE_DATA_KI_GRAPH_FIELD' as const;
const CHANGE_DATA_KI_GRAPH_DATE_TYPE =
  'CHANGE_DATA_KI_GRAPH_DATE_TYPE' as const;
const CHANGE_DATA_KI_GRAPH_SELECT_HALLS =
  'CHANGE_DATA_KI_GRAPH_SELECT_HALLS' as const;
const HIDE_DATA_KI_GRAPH = 'HIDE_DATA_KI_GRAPH' as const;
const FETCH_DATA_KI_GRAPH = 'FETCH_DATA_KI_GRAPH' as const;
const FETCH_DATA_KI_GRAPH_FROM_FAVORITE =
  'FETCH_DATA_KI_GRAPH_FROM_FAVORITE' as const;
const FETCH_DATA_KI_GRAPH_REQUEST = 'FETCH_DATA_KI_GRAPH_REQUEST' as const;
const FETCH_DATA_KI_GRAPH_SUCCESS = 'FETCH_DATA_KI_GRAPH_SUCCESS' as const;
const CHANGE_DATA_KI_GRAPH_DK_SIS_SEARCH_PARAMS =
  'CHANGE_DATA_KI_GRAPH_DK_SIS_SEARCH_PARAMS' as const;
const SET_DATA_KI_GRAPH_SLOTS = 'SET_DATA_KI_GRAPH_SLOTS' as const;
const SET_DATA_KI_GRAPH_GRAPH_LIST = 'SET_DATA_KI_GRAPH_GRAPH_LIST' as const;
const RENEW_DATA_KI_GRAPH = 'RENEW_DATA_KI_GRAPH' as const;
const CHANGE_DATA_KI_GRAPH_OPENED_QUERY_PARAMETER =
  'CHANGE_DATA_KI_GRAPH_OPENED_QUERY_PARAMETER' as const;

export const DataKiGraphActionTypes = {
  SHOW_DATA_KI_GRAPH_LOADING,
  HIDE_DATA_KI_GRAPH_LOADING,
  CHANGE_DATA_KI_GRAPH_FIELD,
  CHANGE_DATA_KI_GRAPH_DATE_TYPE,
  CHANGE_DATA_KI_GRAPH_SELECT_HALLS,
  HIDE_DATA_KI_GRAPH,
  FETCH_DATA_KI_GRAPH,
  FETCH_DATA_KI_GRAPH_FROM_FAVORITE,
  FETCH_DATA_KI_GRAPH_REQUEST,
  FETCH_DATA_KI_GRAPH_SUCCESS,
  CHANGE_DATA_KI_GRAPH_DK_SIS_SEARCH_PARAMS,
  SET_DATA_KI_GRAPH_SLOTS,
  SET_DATA_KI_GRAPH_GRAPH_LIST,
  RENEW_DATA_KI_GRAPH,
  CHANGE_DATA_KI_GRAPH_OPENED_QUERY_PARAMETER,
};

// Action Creators

// ローディングを表示する
function showLoadingAction() {
  return {
    type: SHOW_DATA_KI_GRAPH_LOADING,
    payload: { loadingState: 'loading' as const },
  };
}

// ローディングを非表示にする
function hideLoadingAction() {
  return {
    type: HIDE_DATA_KI_GRAPH_LOADING,
    payload: { loadingState: 'loaded' as const },
  };
}

// グラフの表示項目を変更する
function changeFieldAction(field: GraphFieldType) {
  return {
    type: CHANGE_DATA_KI_GRAPH_FIELD,
    payload: { field },
  };
}

// グラフの表示期間を変更する
function changeDateTypeAction(dateType: GraphDateType) {
  return {
    type: CHANGE_DATA_KI_GRAPH_DATE_TYPE,
    payload: { dateType },
  };
}

// グラフに表示する店舗を変更する
function changeSelectHallsAction(selectHalls: string[]) {
  return {
    type: CHANGE_DATA_KI_GRAPH_SELECT_HALLS,
    payload: { selectHalls },
  };
}

// 推移グラフ（店舗比較）を閉じる
function hideDataKiGraphAction() {
  return {
    type: HIDE_DATA_KI_GRAPH,
    payload: { ...initialState },
  };
}

// 指定した条件でグラフを取得する
function fetchDataKiGraphAction(
  queryParams: QueryParameter,
  selectHalls: string[],
  dkSisSearchParams: DkSisSearchParams,
  requestParams?: {
    hallId: string;
    mainFieldId: string;
    mainFieldRowName?: string;
  },
  isSelectedFromMainField?: boolean,
  onlyOneKiTagAverageTagIds?: string[]
) {
  return {
    type: FETCH_DATA_KI_GRAPH,
    payload: {
      queryParams,
      selectHalls,
      dkSisSearchParams,
      requestParams,
      isSelectedFromMainField,
      onlyOneKiTagAverageTagIds,
    },
  };
}

// 指定したグラフ用の検索条件でグラフを取得する（お気に入り適用）
function fetchDataKiGraphFromFavoriteAction(
  params: DataKiGraphParams,
  selectHalls: string[],
  selectedModelReportsKiList?: ModelReportsSettingState['selectedDataKiExpandedRow']
) {
  return {
    type: FETCH_DATA_KI_GRAPH_FROM_FAVORITE,
    payload: { params, selectHalls, selectedModelReportsKiList },
  };
}

// グラフの取得を開始時に呼ぶもの
function fetchDataKiGraphRequestAction() {
  return {
    type: FETCH_DATA_KI_GRAPH_REQUEST,
  };
}

// グラフの取得が成功した際、取得結果をreducerにpayloadで渡す
function fetchDataKiGraphSuccessAction(dataKiGraph: DataKiGraphCollection) {
  return {
    type: FETCH_DATA_KI_GRAPH_SUCCESS,
    payload: { dataKiGraph },
  };
}

// DK-SIS検索条件を変更
function changeDataKiGraphDkSisSearchParamsAction(params: DkSisSearchParams) {
  return {
    type: CHANGE_DATA_KI_GRAPH_DK_SIS_SEARCH_PARAMS,
    payload: { params },
  };
}

function setDataKiGraphSlotsAction(graphSlots: DataKiGraphSlotConfig[]) {
  return {
    type: SET_DATA_KI_GRAPH_SLOTS,
    payload: { graphSlots },
  };
}

function setDataKiGraphGraphListAction(graphSlots: DataKiGraphSlotConfig[]) {
  return {
    type: SET_DATA_KI_GRAPH_GRAPH_LIST,
    payload: { graphSlots },
  };
}

// グラフの取得に失敗した際、payloadにエラー内容を入れる
function renewDataKiGraphAction() {
  return {
    type: RENEW_DATA_KI_GRAPH,
  };
}

/**
 * グラフを開いた行の QueryParameter を登録する
 *
 * 意図:
 * - 「実際にユーザーがどの行を起点にグラフを開いたか」を明示的に保存し、
 *   平均/合計行の判定をより確実に行うため
 */
function changeDataKiGraphOpenedQueryParameterAction(
  queryParameter: QueryParameter
) {
  return {
    type: CHANGE_DATA_KI_GRAPH_OPENED_QUERY_PARAMETER,
    payload: { queryParameter },
  };
}

export const DataKiGraphActionCreators = {
  showLoadingAction,
  hideLoadingAction,
  changeFieldAction,
  changeDateTypeAction,
  changeSelectHallsAction,
  hideDataKiGraphAction,
  fetchDataKiGraphAction,
  fetchDataKiGraphFromFavoriteAction,
  fetchDataKiGraphRequestAction,
  fetchDataKiGraphSuccessAction,
  changeDataKiGraphDkSisSearchParamsAction,
  setDataKiGraphSlotsAction,
  setDataKiGraphGraphListAction,
  renewDataKiGraphAction,
  changeDataKiGraphOpenedQueryParameterAction,
};

// Actions

export type FetchDataKiGraphAction = ReturnType<typeof fetchDataKiGraphAction>;
export type FetchDataKiGraphFromFavoriteAction = ReturnType<
  typeof fetchDataKiGraphFromFavoriteAction
>;
type HideDataKiGraphAction = ReturnType<typeof hideDataKiGraphAction>;
export type ChangeFieldAction = ReturnType<typeof changeFieldAction>;
export type ChangeDateTypeAction = ReturnType<typeof changeDateTypeAction>;
type ChangeSelectHallsAction = ReturnType<typeof changeSelectHallsAction>;
export type ChangeDataKiGraphDkSisSearchParamsAction = ReturnType<
  typeof changeDataKiGraphDkSisSearchParamsAction
>;
export type SetDataKiGraphSlotsAction = ReturnType<
  typeof setDataKiGraphSlotsAction
>;
export type SetDataKiGraphGraphListAction = ReturnType<
  typeof setDataKiGraphGraphListAction
>;

type DataKiGraphAction =
  | FetchDataKiGraphAction
  | FetchDataKiGraphFromFavoriteAction
  | HideDataKiGraphAction
  | ChangeFieldAction
  | ChangeDateTypeAction
  | ChangeSelectHallsAction
  | SetDataKiGraphSlotsAction
  | SetDataKiGraphGraphListAction
  | ReturnType<typeof showLoadingAction>
  | ReturnType<typeof hideLoadingAction>
  | ReturnType<typeof fetchDataKiGraphRequestAction>
  | ReturnType<typeof fetchDataKiGraphSuccessAction>
  | ChangeDataKiGraphDkSisSearchParamsAction
  | ReturnType<typeof renewDataKiGraphAction>
  | ReturnType<typeof changeDataKiGraphOpenedQueryParameterAction>;

// State

type DataKiGraphState = {
  dataKiGraph?: DataKiGraphCollection;
  selectHalls: string[];
  loadingState: LoadingState;
  /** 推移グラフを開いた（またはプルダウンで切り替えた）メイン行の QueryParameter。平均行判定に使用 */
  graphOpenedQueryParameter?: QueryParameter;
};

export const initialState: DataKiGraphState = {
  dataKiGraph: undefined,
  selectHalls: [],
  loadingState: 'prepare',
  graphOpenedQueryParameter: undefined,
};

// Selector

export const dataKiGraphRawSelector = (rootState: RootState) =>
  rootState.dataKiGraph.dataKiGraph;

export const dataKiGraphGraphListSelector = createSelector(
  dataKiGraphRawSelector,
  (rootState: RootState) => rootState.dataKiGraph.selectHalls,
  (graph, selectHalls): DataKiGraphFieldItem[] => {
    if (!graph) {
      return [];
    }
    return graph.graphList.map((item) => ({
      ...item,
      data: filterGraphDataBySelectHalls(item.data, selectHalls),
    }));
  }
);

// 推移グラフ（店舗比較）のデータが存在するか。
// dataKiGraphGraphListSelector.length > 0 と同値だが、重いフィルタ済み配列を購読せず
// boolean だけを購読したい呼び出し側（存在判定・ローディング判定）向けに用意する。
export const dataKiGraphIsExistSelector = createSelector(
  dataKiGraphRawSelector,
  (graph): boolean => (graph?.graphList.length ?? 0) > 0
);

// 推移グラフ（店舗比較）データ。既存コンポーネント互換のため先頭グラフを返す。
// graph / selectHalls が変わった時だけ再計算するようメモ化する（未メモ化だと毎回新参照を返し
// shallowEqual を素通りして全 dispatch で再レンダー＆フィルタ再計算になる）。
export const dataKiGraphSelector = createSelector(
  dataKiGraphRawSelector,
  (rootState: RootState) => rootState.dataKiGraph.selectHalls,
  (graph, selectHalls): DataKiGraph | undefined => {
    const firstItem = graph?.graphList.at(0);
    if (!graph || !firstItem) {
      return undefined;
    }

    return {
      setting: {
        ...graph.setting,
        field: firstItem.field,
        graphSlots: graph.graphList.map((item) => ({
          field: item.field,
          dkSisCondition: item.dkSisCondition,
        })),
      },
      data: filterGraphDataBySelectHalls(firstItem.data, selectHalls),
    };
  }
);

// 現在の検索条件
export function dataKiGraphParamsSelector(rootState: RootState) {
  const graph = rootState.dataKiGraph.dataKiGraph;
  if (!graph) {
    return undefined;
  }
  // 設定(setting.graphSlots)を優先。取得に失敗して graphList に載っていない
  // スロットも設定として保持し、お気に入り保存・再取得で欠落させない。
  // 設定が無い場合のみ graphList から導出する。
  const graphSlots =
    graph.setting.graphSlots && graph.setting.graphSlots.length > 0
      ? graph.setting.graphSlots
      : graph.graphList.map((item) => ({
          field: item.field,
          dkSisCondition: item.dkSisCondition,
        }));
  return {
    ...graph.setting,
    field: graph.graphList.at(0)?.field ?? GRAPH_FIELD_TYPE.OUT,
    ...(graphSlots.length > 0 ? { graphSlots } : {}),
  };
}

// ローディング状態
export function dataKiGraphLoadingSelector(rootState: RootState) {
  return rootState.dataKiGraph.loadingState;
}

// 選択中の表示項目
export function dataKiGraphFieldSelector(rootState: RootState) {
  return (
    rootState.dataKiGraph.dataKiGraph?.graphList.at(0)?.field ??
    GRAPH_FIELD_TYPE.OUT
  );
}

// 選択中の表示期間
export function dataKiGraphDateTypeSelector(rootState: RootState) {
  return (
    rootState.dataKiGraph.dataKiGraph?.setting.dateType ?? GRAPH_DATE_TYPE.DAILY
  );
}

// 選択中の店舗
export function dataKiGraphSelectHallsSelector(rootState: RootState) {
  return rootState.dataKiGraph.selectHalls;
}

/** 推移グラフを開いた行の queryParameter（平均/合計行は value が 'null'） */
export function dataKiGraphOpenedQueryParameterSelector(rootState: RootState) {
  return rootState.dataKiGraph.graphOpenedQueryParameter;
}

// 選択中できる店舗一覧
export function dataKiGraphHallsListSelector(rootState: RootState) {
  const firstGraphData =
    rootState.dataKiGraph.dataKiGraph?.graphList.at(0)?.data;
  if (firstGraphData === undefined) return [];

  const hallsList = firstGraphData.columns.reduce(
    (prev: string[], current, idx) => {
      if (idx === 1) prev.push('null'); // 平均・合計
      if (idx !== 0 && idx !== 1) prev.push(current.name); // 店舗

      return prev;
    },
    []
  );

  // 店舗一覧にDK-SISのデータも入っているので店舗一覧から削除する
  return hallsList.filter((x: string) => !x.toLowerCase().includes('dk-sis'));
}

// Reducer

export function dataKiGraphReducer(
  state = initialState,
  action: DataKiGraphAction
): DataKiGraphState {
  switch (action.type) {
    case FETCH_DATA_KI_GRAPH_SUCCESS:
      return {
        ...state,
        dataKiGraph: action.payload.dataKiGraph,
      };
    case FETCH_DATA_KI_GRAPH_FROM_FAVORITE:
      return {
        ...state,
        selectHalls: action.payload.selectHalls,
      };
    case SHOW_DATA_KI_GRAPH_LOADING:
    case HIDE_DATA_KI_GRAPH_LOADING:
      return {
        ...state,
        loadingState: action.payload.loadingState,
      };
    case CHANGE_DATA_KI_GRAPH_SELECT_HALLS:
      return {
        ...state,
        selectHalls: action.payload.selectHalls,
      };
    case CHANGE_DATA_KI_GRAPH_DATE_TYPE: {
      // 再取得の完了を待たずセレクタの dateType を先行反映する
      // （期間依存の表示・判定を即時に整合させるための楽観更新）
      const graph = state.dataKiGraph;
      if (!graph) {
        return state;
      }
      return {
        ...state,
        dataKiGraph: {
          ...graph,
          setting: {
            ...graph.setting,
            dateType: action.payload.dateType,
          },
        },
      };
    }
    case SET_DATA_KI_GRAPH_GRAPH_LIST: {
      const graph = state.dataKiGraph;
      if (!graph) {
        return state;
      }
      return {
        ...state,
        dataKiGraph: {
          ...graph,
          // 設定(setting.graphSlots)を削除後の構成に同期させ、graphList 由来の
          // 再導出とズレないようにする（お気に入り・再取得の基準を設定に一本化）。
          setting: {
            ...graph.setting,
            graphSlots: action.payload.graphSlots,
          },
          graphList: buildGraphListForNormalizedSlots(
            graph.graphList,
            action.payload.graphSlots
          ),
        },
      };
    }
    case HIDE_DATA_KI_GRAPH:
      return { ...action.payload };
    case CHANGE_DATA_KI_GRAPH_OPENED_QUERY_PARAMETER:
      // グラフを開いた行の QueryParameter を保存する。
      // これにより UI 側で「平均/合計かどうか」を件数一致の判定ではなく
      // 実際にユーザーが開いた行情報で判定できるようになる。
      return {
        ...state,
        graphOpenedQueryParameter: action.payload.queryParameter,
      };
    case RENEW_DATA_KI_GRAPH:
      return initialState;
    default:
      return state;
  }
}
